import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePosSubtitle } from '../PosLayout';
import {
  getTodayCashRegister,
  openCashRegister,
  closeCashRegister,
  getDailySummary,
  getTopProducts,
  getUsers,
  transferOpenOrders,
} from '../../lib/api';
import {
  AlertTriangle,
  ArrowRightLeft,
  Banknote,
  BarChart3,
  CalendarRange,
  ChevronDown,
  Circle,
  CreditCard,
  Euro,
  Loader2,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { extractStornoReason } from '../orderMeta';
import { getCashRegisterUiState } from '../registerState';

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  return fallback;
}

function formatDateInput(date) {
  return format(date, 'yyyy-MM-dd');
}

function formatDateTime(value) {
  if (!value) return '—';
  return format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: de });
}

function StatusBadge({ tone = 'neutral', children }) {
  const tones = {
    positive: 'bg-lime-100 text-lime-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-700',
    neutral: 'bg-muted text-muted-foreground',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function SidebarItem({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-5 py-4 text-left text-base transition-colors ${
        active ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground hover:bg-muted'
      }`}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-foreground' : 'text-muted-foreground'}`} />
      <span>{label}</span>
    </button>
  );
}

function TimelineStep({ title, subtitle, active, isLast }) {
  return (
    <div className="flex items-start gap-5">
      <div className="flex flex-col items-center">
        <div className={`h-5 w-5 rounded-full ${active ? 'bg-lime-500' : 'bg-slate-300'}`} />
        {!isLast ? <div className={`mt-2 w-0.5 flex-1 ${active ? 'bg-lime-300' : 'bg-slate-200'}`} style={{ minHeight: 84 }} /> : null}
      </div>
      <div className="pb-8">
        <p className="text-4xl font-black tracking-tight text-foreground">{title}</p>
        <p className="mt-2 text-lg text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone = 'default' }) {
  const tones = {
    default: 'text-foreground',
    positive: 'text-lime-700',
    warning: 'text-amber-700',
    danger: 'text-red-700',
  };

  return (
    <div className="border border-border bg-card px-4 py-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      </div>
      <p className={`text-3xl font-black tabular-nums ${tones[tone] || tones.default}`}>{value}</p>
    </div>
  );
}

function KassenabschlussPanel({ register, isLoading, canCloseRegister }) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  const viewState = getCashRegisterUiState(register);
  const isOpen = viewState === 'open';
  const isClosed = viewState === 'closed';
  const needsReview = viewState === 'review_required';
  const showClosingSummary = isClosed;

  const timelineNodes = [];
  if (register?.opened_at) {
    timelineNodes.push({
      title: formatDateTime(register.opened_at),
      subtitle: register?.opened_by_name || register?.opened_by || 'Tag begonnen',
      active: true,
    });
  } else if (needsReview) {
    timelineNodes.push({
      title: 'Status prüfen',
      subtitle: 'Es gibt bereits Umsätze oder Bewegungen ohne dokumentierte Startbuchung.',
      active: true,
    });
  } else if (isOpen) {
    timelineNodes.push({
      title: 'Aktiv',
      subtitle: 'Kasse ist geöffnet und nimmt Buchungen an.',
      active: true,
    });
  } else {
    timelineNodes.push({
      title: 'Noch nicht geöffnet',
      subtitle: 'Der Tag wurde heute noch nicht gestartet.',
      active: false,
    });
  }

  if (register?.closed_at) {
    timelineNodes.push({
      title: formatDateTime(register.closed_at),
      subtitle: register?.closed_by_name || register?.closed_by || 'Tag beendet',
      active: true,
    });
  } else if (isOpen) {
    timelineNodes.push({
      title: 'Aktiv',
      subtitle: canCloseRegister
        ? 'Manager kann den Tag jetzt sauber abschließen.'
        : 'Der Tagesabschluss ist nur für Manager oder Admin freigeschaltet.',
      active: true,
    });
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex flex-1 items-center justify-center overflow-y-auto p-8">
        <div className="w-full max-w-2xl">
          <div className="mx-auto max-w-xl">
            {timelineNodes.map((node, index) => (
              <TimelineStep
                key={`${node.title}-${index}`}
                title={node.title}
                subtitle={node.subtitle}
                active={node.active}
                isLast={index === timelineNodes.length - 1}
              />
            ))}
          </div>
        </div>
      </div>

      <aside className={`hidden w-[360px] flex-shrink-0 border-l border-border bg-card xl:block ${showClosingSummary ? '' : 'xl:hidden'}`}>
        <div className="border-b border-border px-5 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xl font-bold text-foreground">Abschluss gespeichert</p>
              <p className="mt-1 text-sm text-muted-foreground">Diese Werte gehören zum bereits abgeschlossenen Tag.</p>
            </div>
            <StatusBadge tone="neutral">
              Beendet
            </StatusBadge>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <SummaryTile icon={Euro} label="Anfangsbestand" value={`€${(register?.opening_amount || 0).toFixed(2)}`} />
          <SummaryTile icon={Banknote} label="Barumsatz" value={`€${(register?.cash_sales || 0).toFixed(2)}`} tone="positive" />
          <SummaryTile icon={CreditCard} label="Kartenumsatz" value={`€${(register?.card_sales || 0).toFixed(2)}`} />
          <SummaryTile icon={Receipt} label="Erwartete Kassa" value={`€${(register?.expected_amount || 0).toFixed(2)}`} />
          <SummaryTile icon={AlertTriangle} label="Stornos" value={`${register?.total_cancellations ?? 0}`} tone={(register?.total_cancellations || 0) > 0 ? 'warning' : 'default'} />
        </div>
      </aside>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function SimpleSelect({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-14 w-full appearance-none border-b border-border bg-transparent px-0 text-2xl font-medium text-foreground outline-none transition-colors focus:border-primary"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

function BerichtePanel({
  summary,
  topProducts,
  isLoading,
  reportPreset,
  onPresetChange,
  reportUser,
  onUserChange,
  reportStart,
  reportEnd,
  onStartChange,
  onEndChange,
  currentUserId,
  currentUserName,
}) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  const rangeDifferent = reportStart !== reportEnd;
  const userBreakdown = summary?.user_breakdown || [];
  const userOptionsMap = new Map([['all', { value: 'all', label: 'Alle Benutzer' }]]);

  if (currentUserId) {
    userOptionsMap.set(currentUserId, {
      value: currentUserId,
      label: currentUserName || 'Aktueller Benutzer',
    });
  }

  userBreakdown
    .filter((entry) => entry?.user_id)
    .forEach((entry) => {
      userOptionsMap.set(entry.user_id, {
        value: entry.user_id,
        label: entry.user_name || 'Unbekannt',
      });
    });

  const reportUserOptions = Array.from(userOptionsMap.values());

  return (
    <div className="flex flex-1 overflow-y-auto">
      <div className="flex-1 p-6">
        <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_480px]">
          <div className="space-y-6">
            <section className="border border-border bg-card">
              <div className="grid gap-6 px-6 py-6 md:grid-cols-3">
                <FilterField label="Kassa">
                  <SimpleSelect
                    value="all"
                    onChange={() => {}}
                    options={[{ value: 'all', label: 'Alle Kassen' }]}
                  />
                </FilterField>
                <FilterField label="Benutzer">
                  <SimpleSelect
                    value={reportUser}
                    onChange={onUserChange}
                    options={reportUserOptions}
                  />
                </FilterField>
                <FilterField label="Vorauswahl">
                  <SimpleSelect
                    value={reportPreset}
                    onChange={onPresetChange}
                    options={[
                      { value: 'today', label: 'Heute' },
                      { value: 'yesterday', label: 'Gestern' },
                      { value: 'custom', label: 'Benutzerdefiniert' },
                    ]}
                  />
                </FilterField>
              </div>

              <div className="grid gap-6 border-t border-border px-6 py-6 md:grid-cols-2">
                <FilterField label="Beginn anpassen">
                  <input
                    type="date"
                    value={reportStart}
                    onChange={(event) => onStartChange(event.target.value)}
                    className="h-14 w-full border-b border-border bg-transparent px-0 text-2xl font-medium text-foreground outline-none transition-colors focus:border-primary"
                  />
                </FilterField>
                <FilterField label="Ende anpassen">
                  <input
                    type="date"
                    value={reportEnd}
                    onChange={(event) => onEndChange(event.target.value)}
                    className="h-14 w-full border-b border-border bg-transparent px-0 text-2xl font-medium text-foreground outline-none transition-colors focus:border-primary"
                  />
                </FilterField>
              </div>

              {rangeDifferent ? (
                <div className="border-t border-amber-100 bg-amber-50 px-6 py-4 text-sm text-amber-700">
                  Mehrtagesberichte folgen als eigener Aggregations-Flow. Aktuell wird der ausgewählte Endtag geladen.
                </div>
              ) : null}
            </section>

            <section className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-foreground">Erfolgreiche Bestellungen</p>
                <p className="mt-1 text-sm text-muted-foreground">Kennzahlen für den aktuell geladenen Berichtstag.</p>
              </div>
              <div className="grid gap-4 xl:grid-cols-4">
                <SummaryTile icon={Receipt} label="Anzahl" value={`${summary?.total_orders ?? 0}`} />
                <SummaryTile icon={Euro} label="Umsatz" value={`€${summary?.total_sales?.toFixed(2) ?? '0.00'}`} tone="positive" />
                <SummaryTile icon={CreditCard} label="Karte" value={`€${summary?.card_total?.toFixed(2) ?? '0.00'}`} />
                <SummaryTile icon={Banknote} label="Bar" value={`€${summary?.cash_total?.toFixed(2) ?? '0.00'}`} />
              </div>
            </section>

            <section className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-foreground">Storno und Differenzen</p>
                <p className="mt-1 text-sm text-muted-foreground">Dokumentiert und direkt auswertbar.</p>
              </div>
              <div className="grid gap-4 xl:grid-cols-3">
                <SummaryTile icon={AlertTriangle} label="Stornos" value={`${summary?.cancellation_count ?? 0}`} tone={(summary?.cancellation_count || 0) > 0 ? 'warning' : 'default'} />
                <SummaryTile icon={Euro} label="Storno-Summe" value={`€${summary?.cancellation_amount?.toFixed(2) ?? '0.00'}`} tone={(summary?.cancellation_amount || 0) > 0 ? 'danger' : 'default'} />
                <SummaryTile icon={TrendingUp} label="Netto nach Storno" value={`€${summary?.sales_after_cancellations?.toFixed(2) ?? '0.00'}`} />
              </div>
            </section>

            <section className="border border-border bg-card">
              <div className="border-b border-border px-5 py-4">
                <p className="text-xl font-bold text-foreground">Umsatz pro Benutzer</p>
                <p className="mt-1 text-sm text-muted-foreground">Detaillierte Tageswerte pro Kellner inklusive Storno und Zahlungsart.</p>
              </div>

              {userBreakdown.length === 0 ? (
                <div className="px-5 py-6 text-sm text-muted-foreground">Für den gewählten Zeitraum sind noch keine Benutzerumsätze vorhanden.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {userBreakdown.map((entry) => (
                    <div key={entry.user_id || entry.user_name} className="px-5 py-4">
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                        <div>
                          <p className="text-lg font-bold text-foreground">{entry.user_name || 'Unbekannt'}</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {entry.orders || 0} Bestellungen · Ø €{(entry.avg_ticket || 0).toFixed(2)} pro Bon
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 xl:min-w-[460px]">
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Umsatz</p>
                            <p className="mt-1 text-base font-bold text-emerald-700">€{(entry.sales_total || 0).toFixed(2)}</p>
                          </div>
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Bar</p>
                            <p className="mt-1 text-base font-bold text-foreground">€{(entry.cash_total || 0).toFixed(2)}</p>
                          </div>
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Karte</p>
                            <p className="mt-1 text-base font-bold text-foreground">€{(entry.card_total || 0).toFixed(2)}</p>
                          </div>
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Trinkgeld</p>
                            <p className="mt-1 text-base font-bold text-foreground">€{(entry.tip_total || 0).toFixed(2)}</p>
                          </div>
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Rabatt</p>
                            <p className="mt-1 text-base font-bold text-foreground">€{(entry.discount_total || 0).toFixed(2)}</p>
                          </div>
                          <div className="border border-border bg-muted px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Stornos</p>
                            <p className={`mt-1 text-base font-bold ${(entry.cancellation_count || 0) > 0 ? 'text-amber-700' : 'text-foreground'}`}>
                              {entry.cancellation_count || 0} · €{(entry.cancellation_amount || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="space-y-6">
            <section className="border border-border bg-card">
              <div className="border-b border-border px-5 py-4">
                <p className="text-xl font-bold text-foreground">Top Artikel</p>
                <p className="mt-1 text-sm text-muted-foreground">Welche Produkte heute den meisten Umsatz gemacht haben.</p>
              </div>
              <div className="divide-y divide-slate-100">
                {topProducts.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-muted-foreground">Keine Verkäufe für den gewählten Zeitraum.</div>
                ) : (
                  topProducts.map((product, index) => (
                    <div key={product.product_id} className="grid grid-cols-[32px_minmax(0,1fr)_auto] gap-4 px-5 py-4">
                      <span className="text-xl font-black text-slate-300">{index + 1}</span>
                      <div>
                        <p className="text-lg font-bold text-foreground">{product.product_name}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{product.quantity} verkauft</p>
                      </div>
                      <span className="text-lg font-bold tabular-nums text-foreground">€{product.total?.toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="border border-border bg-card">
              <div className="border-b border-border px-5 py-4">
                <p className="text-xl font-bold text-foreground">Storno-Dokumentation</p>
              </div>
              <div className="divide-y divide-slate-100">
                {(summary?.cancelled_orders || []).length === 0 ? (
                  <div className="px-5 py-6 text-sm text-muted-foreground">Heute wurden keine Stornos dokumentiert.</div>
                ) : (
                  summary.cancelled_orders.slice(0, 6).map((order) => (
                    <div key={order.id} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-lg font-bold text-foreground">#{order.order_number}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{order.table_name || order.customer_name || 'Bestellung'}</p>
                        </div>
                        <span className="text-lg font-bold text-red-700">€{order.total?.toFixed(2)}</span>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">
                        {extractStornoReason(order.notes) || order.notes || 'Kein Grund hinterlegt.'}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

const TAB_SUBTITLES = {
  kassenabschluss: 'Kassenabschluss',
  berichte: 'Berichte',
};

export default function PosBerichtswesen() {
  const { user } = useAuth();
  const { setSubtitle } = usePosSubtitle();
  const [activeTab, setActiveTab] = useState('kassenabschluss');
  const [register, setRegister] = useState(null);
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [cashiers, setCashiers] = useState([]);
  const [targetCashierId, setTargetCashierId] = useState('');
  const [isLoadingCashiers, setIsLoadingCashiers] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [reportPreset, setReportPreset] = useState('today');
  const [reportUser, setReportUser] = useState('all');
  const [reportStart, setReportStart] = useState(formatDateInput(new Date()));
  const [reportEnd, setReportEnd] = useState(formatDateInput(new Date()));
  const hasPerm = useCallback((perm) => {
    if (user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'manager') return true;
    const permissions = new Set([
      ...(Array.isArray(user?.effective_permissions) ? user.effective_permissions : []),
      ...(Array.isArray(user?.permissions) ? user.permissions : []),
    ]);
    return permissions.has('all') || permissions.has(perm);
  }, [user]);

  const reportDate = useMemo(() => reportEnd || reportStart, [reportEnd, reportStart]);
  const registerViewState = useMemo(() => getCashRegisterUiState(register), [register]);
  const canCloseRegister = hasPerm('kassenabschluss_durchfuehren') || hasPerm('kassa_schliessen');
  const canOpenNewShift = Boolean(register?.can_open_new_shift) || ['closed', 'not_started'].includes(registerViewState);
  const autoCashCloseAmount = Number(register?.auto_cash_close_amount || 0);
  const autoCashCloseOrders = Number(register?.auto_cash_close_orders || 0);
  const expectedCash = useMemo(
    () => (register?.expected_amount || ((register?.opening_amount || 0) + (register?.cash_sales || 0))) + autoCashCloseAmount,
    [register, autoCashCloseAmount],
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const selectedCashierId = reportUser === 'all' ? null : reportUser;
      const [reg, sum, top] = await Promise.all([
        getTodayCashRegister(),
        getDailySummary(reportDate, selectedCashierId),
        getTopProducts(reportDate, 8, selectedCashierId),
      ]);
      setRegister(reg);
      setSummary(sum);
      setTopProducts(top);
    } catch (err) {
      console.error(err);
      toast.error('Berichtsdaten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [reportDate, reportUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSubtitle(TAB_SUBTITLES[activeTab] || '');
    return () => setSubtitle('');
  }, [activeTab, setSubtitle]);

  const handlePresetChange = (value) => {
    const today = new Date();
    if (value === 'today') {
      const formatted = formatDateInput(today);
      setReportStart(formatted);
      setReportEnd(formatted);
    } else if (value === 'yesterday') {
      const yesterday = formatDateInput(subDays(today, 1));
      setReportStart(yesterday);
      setReportEnd(yesterday);
    }
    setReportPreset(value);
  };

  const handleOpen = async () => {
    if (!openingAmount) {
      toast.error('Bitte Anfangsbestand eingeben');
      return;
    }

    try {
      await openCashRegister(parseFloat(openingAmount), user?.id);
      toast.success('Tag begonnen');
      setShowOpenDialog(false);
      setOpeningAmount('');
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Öffnen'));
    }
  };

  const handleClose = async () => {
    if (!closingAmount) {
      toast.error('Bitte Kassenstand eingeben');
      return;
    }

    try {
      const result = await closeCashRegister(parseFloat(closingAmount), user?.id);
      if (result.difference !== 0) {
        toast.warning(`Kassendifferenz: €${result.difference?.toFixed(2)}`);
      } else {
        const reportSuffix = result?.z_number ? ` · Abschluss #${result.z_number}` : '';
        toast.success(`Tag beendet und im System gespeichert${reportSuffix}`);
      }
      setShowCloseDialog(false);
      setClosingAmount('');
      loadData();
    } catch (error) {
      if ([404, 409].includes(error?.response?.status)) {
        loadData();
      }
      toast.error(getApiErrorMessage(error, 'Fehler beim Schließen'));
    }
  };

  const isOpen = registerViewState === 'open' || registerViewState === 'review_required';
  const isClosed = registerViewState === 'closed';

  const openTransferDialog = async () => {
    if (!isOpen) {
      toast.error('Für die Übergabe muss eine Kasse geöffnet sein.');
      return;
    }
    setShowTransferDialog(true);
    setTargetCashierId('');
    setIsLoadingCashiers(true);
    try {
      const users = await getUsers();
      const userList = Array.isArray(users) ? users : users?.users || [];
      setCashiers(userList.filter((entry) => entry?.is_active !== false && entry?.id !== user?.id));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Mitarbeiter konnten nicht geladen werden'));
    } finally {
      setIsLoadingCashiers(false);
    }
  };

  const handleTransferOpenOrders = async () => {
    if (!targetCashierId) {
      toast.error('Bitte einen Mitarbeiter für die Übergabe auswählen.');
      return;
    }
    setIsTransferring(true);
    try {
      const result = await transferOpenOrders(targetCashierId);
      toast.success(`${result.transferred_count} offene Bon${result.transferred_count === 1 ? '' : 's'} an ${result.cashier?.name || 'Mitarbeiter'} übergeben`);
      setShowTransferDialog(false);
      setTargetCashierId('');
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Offene Bons konnten nicht übergeben werden'));
    } finally {
      setIsTransferring(false);
    }
  };
  const bottomButton =
    activeTab === 'kassenabschluss'
      ? isClosed && !canOpenNewShift
        ? {
            label: 'Tag Beendet',
            sub: 'ABSCHLUSS GESPEICHERT',
            onClick: () => {},
            color: 'bg-slate-700 hover:bg-slate-700',
            disabled: true,
          }
        : isOpen
          ? canCloseRegister
            ? {
                label: 'Tag Beenden',
                sub: 'MANAGER-ABSCHLUSS',
                onClick: () => setShowCloseDialog(true),
                color: 'bg-red-600 hover:bg-red-700',
              }
            : {
                label: 'Manager Erforderlich',
                sub: 'NUR MANAGER ODER ADMIN',
                onClick: () => toast.error('Der Tagesabschluss ist nur für Manager oder Admin erlaubt.'),
                color: 'bg-amber-600 hover:bg-amber-700',
              }
          : {
            label: 'Tag Beginnen',
            sub: isClosed ? 'NEUE SCHICHT STARTEN' : 'KEIN TAG AKTIV',
            onClick: () => setShowOpenDialog(true),
            color: 'bg-lime-500 hover:bg-lime-600',
          }
      : {
          label: 'Anzeigen',
          sub: 'BERICHT AKTUALISIEREN',
          onClick: loadData,
          color: 'bg-lime-500 hover:bg-lime-600',
        };

  return (
    <div className="flex h-full overflow-hidden bg-muted" data-testid="pos-berichtswesen">
      <aside className="hidden w-72 flex-shrink-0 border-r border-border bg-card lg:flex lg:flex-col">
        <div className="border-b border-border px-5 py-5">
          <p className="text-2xl font-bold text-foreground">Berichtswesen</p>
          <p className="mt-1 text-sm text-muted-foreground">KASSA 01 · {user?.name || 'Kellner'}</p>
        </div>
        <div className="flex-1 py-2">
          <SidebarItem
            active={activeTab === 'kassenabschluss'}
            icon={Circle}
            label="Kassenabschluss"
            onClick={() => setActiveTab('kassenabschluss')}
          />
          <SidebarItem
            active={activeTab === 'berichte'}
            icon={BarChart3}
            label="Berichte"
            onClick={() => setActiveTab('berichte')}
          />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="border-b border-border bg-muted px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {activeTab === 'kassenabschluss' ? 'Kassenabschluss' : 'Berichte'}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {activeTab === 'kassenabschluss'
                  ? 'Tagesstatus, Bewegungen und Öffnen/Schließen der Kassa.'
                  : 'Kennzahlen, Filter und Tagesauswertung in kompakter POS-Ansicht.'}
              </p>
            </div>
            {activeTab === 'kassenabschluss' && isOpen ? (
              <button
                type="button"
                onClick={openTransferDialog}
                className="inline-flex shrink-0 items-center gap-2 border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-muted sm:text-sm"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Offene Bons übergeben
              </button>
            ) : null}
            {activeTab === 'berichte' ? (
              <div className="hidden items-center gap-2 text-sm text-muted-foreground xl:flex">
                <CalendarRange className="h-4 w-4" />
                <span>{reportStart}</span>
                <span>bis</span>
                <span>{reportEnd}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="border-b border-border bg-card px-3 py-3 lg:hidden">
          <div className="flex gap-2 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('kassenabschluss')}
              className={`whitespace-nowrap px-3 py-2 text-sm font-semibold transition-colors ${
                activeTab === 'kassenabschluss' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              Kassenabschluss
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('berichte')}
              className={`whitespace-nowrap px-3 py-2 text-sm font-semibold transition-colors ${
                activeTab === 'berichte' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              Berichte
            </button>
          </div>
        </div>

        {activeTab === 'kassenabschluss' ? (
          <KassenabschlussPanel register={register} isLoading={isLoading} canCloseRegister={canCloseRegister} />
        ) : (
          <BerichtePanel
            summary={summary}
            topProducts={topProducts}
            isLoading={isLoading}
            reportPreset={reportPreset}
            onPresetChange={handlePresetChange}
            reportUser={reportUser}
            onUserChange={setReportUser}
            reportStart={reportStart}
            reportEnd={reportEnd}
            onStartChange={(value) => {
              setReportPreset('custom');
              setReportStart(value);
            }}
            onEndChange={(value) => {
              setReportPreset('custom');
              setReportEnd(value);
            }}
            currentUserId={user?.id}
            currentUserName={user?.name}
          />
        )}

        <button
          type="button"
          onClick={bottomButton.onClick}
          disabled={bottomButton.disabled}
          className={`flex-shrink-0 px-5 py-4 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-100 ${bottomButton.color}`}
        >
          <span className="block text-2xl font-bold">{bottomButton.label}</span>
          <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
            {bottomButton.sub}
          </span>
        </button>
      </div>

      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="pos-workflow-dialog max-w-md rounded-none border">
          <DialogHeader className="pos-dialog-header -mx-6 -mt-6 px-6 py-4">
            <DialogTitle>Tag beginnen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Bitte gib den Anfangsbestand der Kasse ein.</p>
            <div>
              <Label>Anfangsbestand (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={openingAmount}
                onChange={(event) => setOpeningAmount(event.target.value)}
                placeholder="0.00"
                className="pos-dialog-field mt-1 text-xl font-mono"
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={handleOpen}
              className="pos-dialog-primary w-full py-3 font-bold"
            >
              Tag beginnen
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="pos-workflow-dialog max-w-md rounded-none border">
          <DialogHeader className="pos-dialog-header -mx-6 -mt-6 px-6 py-4">
            <DialogTitle>Tag beenden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Der Abschluss wird nach Bestätigung im System gespeichert. Die Tageszahlen zeigen wir hier nur für den Abschluss,
              nicht permanent auf der Kassenabschluss-Seite.
            </p>
            {autoCashCloseOrders > 0 ? (
              <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {register?.requires_closing_previous_shift ? 'Die vorherige Schicht muss jetzt beendet werden. ' : ''}
                {autoCashCloseOrders} offene Bestellung{autoCashCloseOrders === 1 ? '' : 'en'} werden dabei automatisch als Barzahlung abgeschlossen (€{autoCashCloseAmount.toFixed(2)}).
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <SummaryTile icon={Euro} label="Anfangsbestand" value={`€${(register?.opening_amount || 0).toFixed(2)}`} />
              <SummaryTile icon={Banknote} label="Barumsatz" value={`€${(register?.cash_sales || 0).toFixed(2)}`} tone="positive" />
              <SummaryTile icon={CreditCard} label="Kartenumsatz" value={`€${(register?.card_sales || 0).toFixed(2)}`} />
              <SummaryTile icon={Receipt} label="Erwartete Kassa" value={`€${expectedCash.toFixed(2)}`} />
              <div className="col-span-2">
                <SummaryTile icon={AlertTriangle} label="Stornos" value={`${register?.total_cancellations ?? summary?.cancellation_count ?? 0}`} tone={((register?.total_cancellations ?? summary?.cancellation_count ?? 0) > 0) ? 'warning' : 'default'} />
              </div>
            </div>

            {(summary?.user_breakdown || []).length > 0 ? (
              <div className="space-y-3 border-t border-border pt-4">
                <div>
                  <p className="text-base font-bold text-foreground">Benutzer im Tagesabschluss</p>
                  <p className="mt-1 text-sm text-muted-foreground">Umsatzübersicht pro Benutzer für diese Firmenschicht.</p>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                  {(summary?.user_breakdown || []).map((entry) => (
                    <div key={entry.user_id || entry.user_name} className="border border-border bg-muted px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-foreground">{entry.user_name || 'Unbekannt'}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {entry.orders || 0} Bons · Stornos {entry.cancellation_count || 0}
                          </p>
                        </div>
                        <p className="text-lg font-bold text-emerald-700">€{(entry.sales_total || 0).toFixed(2)}</p>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>Bar: €{(entry.cash_total || 0).toFixed(2)}</div>
                        <div>Karte: €{(entry.card_total || 0).toFixed(2)}</div>
                        <div>Trinkgeld: €{(entry.tip_total || 0).toFixed(2)}</div>
                        <div>Rabatt: €{(entry.discount_total || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div>
              <Label>Tatsächlicher Kassenbestand (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={closingAmount}
                onChange={(event) => setClosingAmount(event.target.value)}
                placeholder={expectedCash.toFixed(2)}
                className="pos-dialog-field mt-1 text-xl font-mono"
                autoFocus
              />
            </div>
            {!canCloseRegister ? (
              <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Nur Manager oder Admin dürfen den Tagesabschluss bestätigen.
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleClose}
              disabled={!canCloseRegister}
              className="w-full bg-red-600 py-3 font-bold text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
            >
              Tag beenden
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="pos-workflow-dialog max-w-md rounded-none border">
          <DialogHeader className="pos-dialog-header -mx-6 -mt-6 px-6 py-4">
            <DialogTitle>Offene Bons übergeben</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Alle eigenen offenen und geparkten Bons werden an den ausgewählten Mitarbeiter übergeben. Die aktive Kasse bleibt dabei unverändert.
            </p>
            <div>
              <Label htmlFor="target-cashier">Mitarbeiter</Label>
              <select
                id="target-cashier"
                value={targetCashierId}
                onChange={(event) => setTargetCashierId(event.target.value)}
                disabled={isLoadingCashiers || isTransferring}
                className="pos-dialog-field mt-1 h-12 w-full px-3 text-base disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">{isLoadingCashiers ? 'Mitarbeiter werden geladen ...' : 'Mitarbeiter auswählen'}</option>
                {cashiers.map((cashier) => (
                  <option key={cashier.id} value={cashier.id}>{cashier.name || cashier.email || 'Mitarbeiter'}</option>
                ))}
              </select>
            </div>
            {!isLoadingCashiers && cashiers.length === 0 ? (
              <div className="border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                Es ist kein weiterer aktiver Mitarbeiter verfügbar.
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setShowTransferDialog(false)} disabled={isTransferring} className="pos-dialog-secondary py-3 font-bold">Abbrechen</button>
              <button type="button" onClick={handleTransferOpenOrders} disabled={!targetCashierId || isTransferring} className="pos-dialog-primary py-3 font-bold disabled:cursor-not-allowed disabled:opacity-50">
                {isTransferring ? 'Übergabe läuft ...' : 'Übergeben'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
